import { useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useCountry } from "@/lib/CountryContext";
import { SUPPORTED_LANGUAGES } from "@/i18n/config";

/**
 * Compact country + language picker for the header.
 *
 * The language column is filtered by the visitor's current country —
 * e.g. Hong Kong shows English + Traditional + Simplified Chinese,
 * Singapore shows English + Simplified Chinese, and English-only
 * markets (AU/UK/NZ/US) hide the language column entirely.
 */
export default function CountryLanguageSelector({ compact = false }) {
    const { country, countryInfo, countries, setCountry, setLanguage } = useCountry();
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);

    const activeLang = i18n.resolvedLanguage || i18n.language || "en";
    // Language options offered in the visitor's current country.
    const allowedLangCodes = countryInfo.languages;
    const availableLangs = SUPPORTED_LANGUAGES.filter((l) =>
        allowedLangCodes.includes(l.code)
    );
    const hideLangColumn = availableLangs.length <= 1;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    data-testid="country-language-trigger"
                    className={`inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white hover:border-navy-300 transition-colors ${
                        compact ? "px-3 py-1.5 text-xs" : "px-3.5 py-2 text-sm"
                    } text-navy-700`}
                    aria-label={`${countryInfo.name} · ${activeLang}`}
                >
                    <Globe size={compact ? 13 : 15} className="text-navy-500" />
                    <span className="font-medium">
                        {countryInfo.flag} {country}
                    </span>
                    <span className="text-navy-400">·</span>
                    <span className="uppercase tracking-wider text-navy-500 text-[11px]">
                        {activeLang.split("-")[0]}
                    </span>
                    <ChevronDown size={12} className="text-navy-400 -ml-0.5" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className={`p-0 border border-gray-200 rounded-2xl overflow-hidden ${
                    hideLangColumn ? "w-[220px]" : "w-[400px]"
                }`}
                data-testid="country-language-popover"
            >
                <div className={hideLangColumn ? "" : "grid grid-cols-[1fr_1.1fr] divide-x divide-gray-100"}>
                    {/* Country column */}
                    <div className="p-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-navy-400 px-2 py-1.5">
                            Country
                        </div>
                        <ul className="space-y-0.5">
                            {countries.map((c) => (
                                <li key={c.code}>
                                    <button
                                        data-testid={`country-option-${c.code}`}
                                        onClick={() => {
                                            setCountry(c.code);
                                            setOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-cream transition-colors ${
                                            country === c.code
                                                ? "text-navy-700 font-medium"
                                                : "text-navy-500"
                                        }`}
                                    >
                                        <span className="text-base leading-none">{c.flag}</span>
                                        <span className="flex-1 text-left">{c.name}</span>
                                        {country === c.code && (
                                            <Check size={14} className="text-gold-600" />
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {/* Language column — hidden entirely for single-language markets */}
                    {!hideLangColumn && (
                        <div className="p-3">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-navy-400 px-2 py-1.5">
                                Language
                            </div>
                            <ul className="space-y-0.5">
                                {availableLangs.map((lang) => (
                                    <li key={lang.code}>
                                        <button
                                            data-testid={`lang-option-${lang.code}`}
                                            onClick={() => {
                                                setLanguage(lang.code);
                                                setOpen(false);
                                            }}
                                            className={`w-full flex flex-col items-start px-2 py-1.5 rounded-lg text-sm hover:bg-cream transition-colors ${
                                                activeLang === lang.code
                                                    ? "text-navy-700 font-medium"
                                                    : "text-navy-500"
                                            }`}
                                        >
                                            <span className="flex items-center gap-2 w-full">
                                                <span className="flex-1 text-left">
                                                    {lang.nativeLabel}
                                                </span>
                                                {activeLang === lang.code && (
                                                    <Check size={14} className="text-gold-600" />
                                                )}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-wider text-navy-400">
                                                {lang.label}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="px-4 py-2.5 bg-cream/60 text-[11px] text-navy-500 leading-relaxed border-t border-gray-100">
                    Content and prices reflect your selected country.
                </div>
            </PopoverContent>
        </Popover>
    );
}
